import { getTrackingTokenFromLocalStorage } from 'helpers/browser';
import { KustomerMain } from 'core/types';
import { createAuthForTrackingToken } from 'core_main/api/pubnub';
import { getStore, updateStore } from 'core_main/store';
import { fetchSchedules } from 'core_main/api/schedules';
import { fetchCurrentTrackingToken } from 'core_main/api/tracking';
import { fetchSettings } from 'core_main/api/settings';
import { importAndInitializePubNub } from 'core_main/helpers/vendors';
import { KustomerErrorResponse } from 'globals/types';
import { createInitCallbackResponse } from './helpers';

export const backoffAuthCall = (retryCount = 0, delay = 100) => {
  return createAuthForTrackingToken()
    .catch((error: KustomerErrorResponse) => {
      const isNotPubNubNetworkFailure = error?.title !== 'PubNubNetworkFailure';

      if (retryCount === 3 || isNotPubNubNetworkFailure) {
        return Promise.reject(error);
      }

      return new Promise((resolve) => setTimeout(resolve, delay));
    })
    .then((res) => {
      if (res) return res;
      return backoffAuthCall(retryCount + 1, delay * 4);
    });
};

export const init: KustomerMain['init'] = (parameters?, cb?) => {
  const originalTrackingToken = getTrackingTokenFromLocalStorage();

  return fetchSettings({
    brandId: parameters?.brandId,
  })
    .then(() => fetchSchedules(parameters?.scheduleId))
    .then(() => {
      const { enabled } = getStore().settings;

      if (enabled === false) {
        console.warn(
          'Kustomer Chat is disabled in your org settings. Initialization will not continue.',
        );

        return Promise.reject();
      }

      if (originalTrackingToken === null) {
        updateStore('isPubNubConnected', false);
        return Promise.reject();
      }

      return undefined;
    })
    .then(fetchCurrentTrackingToken)
    .then((response) => {
      const customerId = response?.data?.relationships?.customer?.data?.id;

      updateStore('customerId', customerId);

      if (customerId === undefined) {
        updateStore('isPubNubConnected', false);
        return Promise.reject();
      }

      const { orgId } = getStore();

      return backoffAuthCall().then(() => {
        return importAndInitializePubNub({
          customerId,
          orgId,
          initCallback: cb,
        });
      });
    })
    .catch((error) => {
      const { settings } = getStore();

      const isEmptyChannelGroupError =
        error?.title === 'EmptyCustomerChannelGroup';

      if (error && !isEmptyChannelGroupError) {
        console.error('Failed to initialize', error);
      }

      const callbackResponse = createInitCallbackResponse(settings);

      if (typeof cb === 'function') {
        if (isEmptyChannelGroupError) {
          cb(callbackResponse);
          return;
        }

        cb(callbackResponse, error);
      }
    });
};
